import { getAppStore, setAppStore } from '@/store';
import { clearGeneration } from '@/utils/storage/generationStorage';
import getGenerationErrorMessage from '@/utils/generation/getGenerationErrorMessage';
import sendGenerateStartAnalytics from '@/utils/analytics/sendGenerateStartAnalytics';
import sendGenerateFailureAnalytics from '@/utils/analytics/sendGenerateFailureAnalytics';
import prepareAutoGeneration from '@/utils/generation/prepareAutoGeneration';
import submitAndPollGeneration from '@/utils/generation/submitAndPollGeneration';
import finalizeAutoGeneration from '@/utils/generation/finalizeAutoGeneration';
import { ToastType } from '@/store/toast/types';

import type { IInput, Preset } from '@/types';

interface AutoGenerationParams {
  webhookId: string;
  selectedPreset: Preset;
  inputs: IInput[];
  toolId: string;
  folderName: string;
}

interface AutoGenerationCallbacks {
  setGenerationError: (error: string) => void;
  showToast: (message: string, type: ToastType) => void;
  onReset: () => void;
  onPlaceholderCreated: (uuid: string) => void;
}

/**
 * Runs the auto-generation flow: creates placeholder, submits webhook,
 * polls for result, updates placeholder with real data.
 *
 * Thin orchestrator over three stages — prepare → submit+poll → finalize.
 * Top-level try/catch normalises errors to a user-facing message + analytics
 * event and rolls back the placeholder.
 */
const runAutoGeneration = async ({
  params,
  callbacks,
}: {
  params: AutoGenerationParams;
  callbacks: AutoGenerationCallbacks;
}) => {
  const { webhookId, selectedPreset, inputs, toolId, folderName } = params;
  const { setGenerationError, showToast, onReset, onPlaceholderCreated } =
    callbacks;

  const preflight = await prepareAutoGeneration({ webhookId, selectedPreset });

  if (!preflight) {
    setAppStore({ isPrimaryActionLoading: false, isGenerating: false });
    onReset();
    return;
  }

  const { placeholderUuid, startedAt, settings } = preflight;
  onPlaceholderCreated(placeholderUuid);
  sendGenerateStartAnalytics(settings);

  let submittedGenerationId: string | undefined;

  const rollback = () => {
    const { contentFiles, setContentFiles } = getAppStore();
    setContentFiles(contentFiles.filter((f) => f.uuid !== placeholderUuid));
    if (submittedGenerationId) {
      clearGeneration(submittedGenerationId);
    }
    onReset();
  };

  try {
    const submitResult = await submitAndPollGeneration({
      webhookId,
      selectedPreset,
      inputs,
      toolId,
      folderName,
      placeholderUuid,
      startedAt,
    });
    submittedGenerationId = submitResult.generationId;

    await finalizeAutoGeneration({
      generationId: submitResult.generationId,
      placeholderUuid,
      presetTitle: selectedPreset.title ?? 'Generated',
      built: submitResult,
    });
  } catch (error) {
    const { userMessage, reason } = getGenerationErrorMessage(error);
    setGenerationError(userMessage);
    showToast(userMessage, ToastType.Error);
    sendGenerateFailureAnalytics(
      Date.now() - startedAt,
      settings,
      `Auto Generation ${reason}`
    );
    rollback();
  } finally {
    setAppStore({
      isPrimaryActionLoading: false,
      isGenerating: false,
    });
  }
};

export default runAutoGeneration;
